import { useCallback, useEffect, useState } from "react";
import { Ban, Check, ChevronLeft, ChevronRight, Clock, ImageOff, Info, RefreshCw, Unlink, X } from "lucide-react";
import {
    getAdminNewsArticles,
    setArticleStatus,
    setArticleCity,
    setArticleCategory,
    setArticleEstablishment,
    setArticleImage,
    scrapeArticleImages,
    setArticleFranchise,
    triggerNewsFetch,
    NewsItem,
    NewsStatus,
    LinkedEstablishment,
    WeekStats,
} from "interfaces/restaurantNews";
import { getFranchises, FranchiseDoc } from "interfaces/establishments";
import { useDebouncedEffect } from "hooks/useDebouncedEffect";
import {
    ArticleRow,
    currentWeekParam,
    formatWeekRange,
    isoWeekNumber,
    shiftWeek,
    FOCUS_RING,
} from "../bo/newsArticleParts";
import BOCommercialNav from "./BOCommercialNav";

/**
 * News moderation for the sales back-office — same job as `/bo/news-articles`, same rows (both
 * pages render `ArticleRow` from `newsArticleParts`), laid out as the rest of `/bo-commercial`:
 * one card per concern, generous rows, and a footnote that says what the page actually does.
 */

type StatusFilter = "all" | "pending" | "approved" | "rejected";

const LIMIT = 30;

export default function CommercialNews() {
    const [items, setItems] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [category, setCategory] = useState("all");
    const [city, setCity] = useState("all");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
    const [cities, setCities] = useState<string[]>([]);
    const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
    const [pendingCount, setPendingCount] = useState(0);
    const [allFranchises, setAllFranchises] = useState<FranchiseDoc[]>([]);
    const [toast, setToast] = useState<string | null>(null);
    const [minScore, setMinScore] = useState("");
    const [maxScore, setMaxScore] = useState("");
    const [franchiseFilter, setFranchiseFilter] = useState<"" | "true" | "false">("");
    const [franceFilter, setFranceFilter] = useState<"" | "true" | "false">("");
    const [hasPhotoFilter, setHasPhotoFilter] = useState<"" | "true" | "false">("");
    const [hasEstablishmentFilter, setHasEstablishmentFilter] = useState<"" | "true" | "false">("");
    const [weekStart, setWeekStart] = useState<string | null>(() => currentWeekParam());
    const [weekStats, setWeekStats] = useState<WeekStats | null>(null);
    const [missingCounts, setMissingCounts] = useState({ noPhoto: 0, noEstablishment: 0, pickable: 0, unscraped: 0 });

    useEffect(() => {
        getFranchises().then(setAllFranchises).catch(() => {});
    }, []);

    const load = useCallback(
        async (
            cat: string,
            cty: string,
            status: StatusFilter,
            pg: number,
            minSc: string,
            maxSc: string,
            franchise: string,
            france: string,
            hasPhoto: string,
            hasEstablishment: string,
            week: string | null
        ) => {
            setLoading(true);
            try {
                const data = await getAdminNewsArticles({
                    category: cat !== "all" ? cat : undefined,
                    city: cty !== "all" ? cty : undefined,
                    status: status !== "all" ? status : "all",
                    minScore: minSc !== "" ? Number(minSc) : undefined,
                    maxScore: maxSc !== "" ? Number(maxSc) : undefined,
                    isFranchise: (franchise as "true" | "false") || undefined,
                    isInFrance: (france as "true" | "false") || undefined,
                    hasPhoto: (hasPhoto as "true" | "false") || undefined,
                    hasEstablishment: (hasEstablishment as "true" | "false") || undefined,
                    weekStart: week ?? undefined,
                    page: pg,
                    limit: LIMIT,
                });
                setItems(data.items);
                setTotal(data.total);
                setTotalPages(data.totalPages);
                setCategoryCounts(data.categoryCounts);
                setCities(data.cities);
                setPendingCount(data.pendingCount);
                setWeekStats(data.weekStats);
                setMissingCounts(data.missingCounts);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useDebouncedEffect(() => {
        load(category, city, statusFilter, page, minScore, maxScore, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart);
    }, [category, city, statusFilter, page, minScore, maxScore, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart, load]);

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(null), 2500);
    };

    const handleSetStatus = async (id: string, status: NewsStatus) => {
        await setArticleStatus(id, status);
        const wasPending = items.find((a) => a._id === id)?.status === "pending";
        if (statusFilter === "pending" && status !== "pending") {
            setItems((prev) => prev.filter((a) => a._id !== id));
            setTotal((t) => Math.max(0, t - 1));
        } else {
            setItems((prev) => prev.map((a) => (a._id === id ? { ...a, status } : a)));
        }
        if (wasPending && status !== "pending") setPendingCount((c) => Math.max(0, c - 1));
        else if (!wasPending && status === "pending") setPendingCount((c) => c + 1);
        showToast(status === "approved" ? "Article approuvé" : status === "rejected" ? "Article rejeté" : "Article en attente");
    };

    const handleSetCity = async (id: string, value: string | null) => {
        // City feeds the matching algorithm — the backend recomputes the suggestion, so merge
        // whatever it comes back with instead of only the city field.
        const updated = await setArticleCity(id, value);
        setItems((prev) =>
            prev.map((a) =>
                a._id === id
                    ? {
                          ...a,
                          city: value,
                          suggestedEstablishment: updated.suggestedEstablishment,
                          suggestedEstablishmentScore: updated.suggestedEstablishmentScore,
                      }
                    : a
            )
        );
    };

    const handleSetCategory = async (id: string, value: "opening" | "closing" | "ownership" | "recruitment") => {
        const updated = await setArticleCategory(id, value);
        setItems((prev) =>
            prev.map((a) =>
                a._id === id
                    ? {
                          ...a,
                          category: value,
                          suggestedEstablishment: updated.suggestedEstablishment,
                          suggestedEstablishmentScore: updated.suggestedEstablishmentScore,
                      }
                    : a
            )
        );
        showToast("Catégorie mise à jour");
    };

    const handleSetImage = async (id: string, image: string | null) => {
        await setArticleImage(id, image);
        setItems((prev) => prev.map((a) => (a._id === id ? { ...a, image, imageManuallySet: image !== null } : a)));
        showToast(image ? "Image de preview mise à jour" : "Image retirée");
    };

    const handleScrapeImages = async (id: string) => {
        const updated = await scrapeArticleImages(id);
        setItems((prev) =>
            prev.map((a) =>
                a._id === id
                    ? {
                          ...a,
                          image: updated.image,
                          imageManuallySet: updated.imageManuallySet,
                          scrapedImages: updated.scrapedImages ?? [],
                          imagesScrapedAt: updated.imagesScrapedAt,
                          imageScrapeOutcome: updated.imageScrapeOutcome,
                          imageScrapeDetail: updated.imageScrapeDetail,
                      }
                    : a
            )
        );
        showToast(`${updated.scrapedImages?.length ?? 0} image(s) trouvée(s)`);
    };

    const handleSetEstablishment = async (id: string, est: LinkedEstablishment | null) => {
        await setArticleEstablishment(id, est?._id ?? null);
        setItems((prev) =>
            prev.map((a) =>
                a._id === id
                    ? { ...a, linkedEstablishment: est, suggestedEstablishment: null, suggestedEstablishmentScore: null }
                    : a
            )
        );
        showToast(est ? `Lié à ${est.name}` : "Établissement dissocié");
    };

    const handleSetFranchise = async (id: string, franchiseName: string | null) => {
        const updated = await setArticleFranchise(id, franchiseName);
        setItems((prev) => prev.map((a) => (a._id === id ? { ...a, franchise: updated.franchise } : a)));
        if (updated.franchise && !allFranchises.find((f) => f._id === updated.franchise!._id)) {
            setAllFranchises((prev) => [...prev, updated.franchise!].sort((a, b) => a.name.localeCompare(b.name, "fr")));
        }
    };

    const handleFetch = async () => {
        setFetching(true);
        try {
            const result = await triggerNewsFetch();
            showToast(`${result.saved} article${result.saved > 1 ? "s" : ""} importé${result.saved > 1 ? "s" : ""}`);
            load(category, city, statusFilter, 1, minScore, maxScore, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart);
            setPage(1);
        } catch {
            showToast("Erreur lors de l'import");
        } finally {
            setFetching(false);
        }
    };

    const resetFilters = () => {
        setCategory("all");
        setCity("all");
        setStatusFilter("all");
        setMinScore("");
        setMaxScore("");
        setFranchiseFilter("");
        setFranceFilter("");
        setHasPhotoFilter("");
        setHasEstablishmentFilter("");
        setWeekStart(currentWeekParam());
        setPage(1);
    };

    const hasActiveFilters =
        category !== "all" ||
        city !== "all" ||
        statusFilter !== "all" ||
        minScore !== "" ||
        maxScore !== "" ||
        franchiseFilter !== "" ||
        franceFilter !== "" ||
        hasPhotoFilter !== "" ||
        hasEstablishmentFilter !== "";

    const totalCount = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
    const selectCls = `rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-ink ${FOCUS_RING}`;
    const weekProgress =
        weekStats && weekStats.total > 0 ? Math.round(((weekStats.total - weekStats.pending) / weekStats.total) * 100) : 0;

    return (
        <div className="min-h-screen bg-canvas">
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-sm text-ink-inverse shadow-float">
                    <span>{toast}</span>
                    <button onClick={() => setToast(null)} className={`rounded ${FOCUS_RING}`}>
                        <X className="h-4 w-4 text-ink-inverse-muted hover:text-ink-inverse" />
                    </button>
                </div>
            )}

            <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
                <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl font-normal tracking-tight text-ink">Actualités</h1>
                        <p className="mt-1 text-sm text-ink-muted">
                            {totalCount} article{totalCount > 1 ? "s" : ""} · {pendingCount} en attente de modération
                        </p>
                    </div>
                    <BOCommercialNav />
                </header>

                {/* ─── the week's backlog ───────────────────────────────────── */}
                <section className="mb-6 overflow-hidden rounded-2xl border border-border bg-surface">
                    <div className="flex items-center gap-4 px-6 py-5">
                        <button
                            onClick={() => {
                                setWeekStart((w) => shiftWeek(w ?? currentWeekParam(), -1));
                                setPage(1);
                            }}
                            title="Semaine précédente"
                            className={`shrink-0 rounded-lg p-2 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>

                        <div className="min-w-0 flex-1">
                            {weekStart ? (
                                <>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <h2 className="text-base font-semibold text-ink">
                                            Semaine {isoWeekNumber(weekStart)}
                                        </h2>
                                        <span className="text-xs text-ink-muted">{formatWeekRange(weekStart)}</span>
                                        {weekStart !== currentWeekParam() && (
                                            <button
                                                onClick={() => {
                                                    setWeekStart(currentWeekParam());
                                                    setPage(1);
                                                }}
                                                className={`rounded text-xs text-accent hover:underline ${FOCUS_RING}`}
                                            >
                                                Revenir à cette semaine
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setWeekStart(null);
                                                setPage(1);
                                            }}
                                            className={`ml-auto rounded text-xs text-ink-faint transition-colors hover:text-ink ${FOCUS_RING}`}
                                        >
                                            Toutes les semaines
                                        </button>
                                    </div>
                                    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
                                        <div
                                            className="h-full rounded-full bg-ink transition-all duration-300"
                                            style={{ width: `${weekProgress}%` }}
                                        />
                                    </div>
                                    <p className="mt-2 text-xs text-ink-muted">
                                        {weekStats
                                            ? weekStats.total === 0
                                                ? "Aucun article reçu cette semaine"
                                                : `${weekStats.pending} restant${weekStats.pending > 1 ? "s" : ""} sur ${weekStats.total} reçu${weekStats.total > 1 ? "s" : ""}`
                                            : "…"}
                                    </p>
                                </>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <h2 className="text-base font-semibold text-ink">Toutes les semaines</h2>
                                    <button
                                        onClick={() => {
                                            setWeekStart(currentWeekParam());
                                            setPage(1);
                                        }}
                                        className={`rounded text-xs text-accent hover:underline ${FOCUS_RING}`}
                                    >
                                        Revenir à la semaine en cours
                                    </button>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => {
                                setWeekStart((w) => shiftWeek(w ?? currentWeekParam(), 1));
                                setPage(1);
                            }}
                            title="Semaine suivante"
                            disabled={weekStart === null}
                            className={`shrink-0 rounded-lg p-2 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink disabled:opacity-30 ${FOCUS_RING}`}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </section>

                {/* ─── the list ─────────────────────────────────────────────── */}
                <section className="overflow-hidden rounded-2xl border border-border bg-surface">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
                        <div>
                            <h2 className="text-base font-semibold text-ink">Modération</h2>
                            <p className="mt-0.5 text-xs text-ink-muted">
                                {loading ? "Chargement…" : `${total} article${total > 1 ? "s" : ""} pour ces filtres`}
                            </p>
                        </div>
                        <button
                            onClick={handleFetch}
                            disabled={fetching}
                            className={`inline-flex items-center gap-1.5 rounded-lg border border-border-strong bg-surface px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-sunken disabled:opacity-50 ${FOCUS_RING}`}
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} />
                            {fetching ? "Import…" : "Importer les flux"}
                        </button>
                    </div>

                    {/* filters */}
                    <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-3">
                        <select
                            value={category}
                            onChange={(e) => {
                                setCategory(e.target.value);
                                setPage(1);
                            }}
                            className={selectCls}
                        >
                            <option value="all">Toutes catégories</option>
                            <option value="opening">Ouvertures ({categoryCounts.opening ?? 0})</option>
                            <option value="closing">Fermetures ({categoryCounts.closing ?? 0})</option>
                            <option value="ownership">Cessions ({categoryCounts.ownership ?? 0})</option>
                            <option value="recruitment">Recrutements ({categoryCounts.recruitment ?? 0})</option>
                        </select>

                        <select
                            value={city}
                            onChange={(e) => {
                                setCity(e.target.value);
                                setPage(1);
                            }}
                            className={selectCls}
                        >
                            <option value="all">Toutes les villes</option>
                            {cities.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>

                        <div className="flex items-center overflow-hidden rounded-lg border border-border">
                            {(["all", "pending", "approved", "rejected"] as StatusFilter[]).map((v) => (
                                <button
                                    key={v}
                                    onClick={() => {
                                        setStatusFilter(v);
                                        setPage(1);
                                    }}
                                    className={`px-3 py-1.5 text-xs transition-colors duration-150 ${
                                        statusFilter === v ? "bg-ink text-ink-inverse" : "text-ink-muted hover:bg-surface-sunken"
                                    }`}
                                >
                                    {v === "all" ? (
                                        "Tous"
                                    ) : v === "pending" ? (
                                        <span className="flex items-center gap-1">
                                            <Clock className="h-3.5 w-3.5" />
                                            En attente
                                        </span>
                                    ) : v === "approved" ? (
                                        <span className="flex items-center gap-1">
                                            <Check className="h-3.5 w-3.5" />
                                            Approuvés
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1">
                                            <Ban className="h-3.5 w-3.5" />
                                            Rejetés
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        <select
                            value={minScore}
                            onChange={(e) => {
                                setMinScore(e.target.value);
                                setPage(1);
                            }}
                            className={selectCls}
                        >
                            <option value="">Score min : tous</option>
                            <option value="90">Score ≥ 90</option>
                            <option value="60">Score ≥ 60</option>
                            <option value="30">Score ≥ 30</option>
                        </select>

                        <select
                            value={maxScore}
                            onChange={(e) => {
                                setMaxScore(e.target.value);
                                setPage(1);
                            }}
                            className={selectCls}
                        >
                            <option value="">Score max : tous</option>
                            <option value="29">Score ≤ 29</option>
                            <option value="59">Score ≤ 59</option>
                            <option value="89">Score ≤ 89</option>
                        </select>

                        <select
                            value={franchiseFilter}
                            onChange={(e) => {
                                setFranchiseFilter(e.target.value as "" | "true" | "false");
                                setPage(1);
                            }}
                            className={selectCls}
                        >
                            <option value="">Franchise : toutes</option>
                            <option value="true">Franchise</option>
                            <option value="false">Non-franchise</option>
                        </select>

                        <select
                            value={franceFilter}
                            onChange={(e) => {
                                setFranceFilter(e.target.value as "" | "true" | "false");
                                setPage(1);
                            }}
                            className={selectCls}
                        >
                            <option value="">Pays : tous</option>
                            <option value="true">France</option>
                            <option value="false">Hors France</option>
                        </select>

                        <button
                            onClick={() => {
                                setHasPhotoFilter((v) => (v === "false" ? "" : "false"));
                                setPage(1);
                            }}
                            title="Articles en attente sans photo"
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors duration-150 ${FOCUS_RING} ${
                                hasPhotoFilter === "false"
                                    ? "border-ink bg-ink text-ink-inverse"
                                    : "border-border text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            <ImageOff className="h-3.5 w-3.5" />
                            Sans photo{missingCounts.noPhoto > 0 && ` (${missingCounts.noPhoto})`}
                        </button>

                        <button
                            onClick={() => {
                                setHasEstablishmentFilter((v) => (v === "false" ? "" : "false"));
                                setPage(1);
                            }}
                            title="Articles en attente sans établissement lié"
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors duration-150 ${FOCUS_RING} ${
                                hasEstablishmentFilter === "false"
                                    ? "border-ink bg-ink text-ink-inverse"
                                    : "border-border text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            <Unlink className="h-3.5 w-3.5" />
                            Sans établissement{missingCounts.noEstablishment > 0 && ` (${missingCounts.noEstablishment})`}
                        </button>

                        {hasActiveFilters && (
                            <button
                                onClick={resetFilters}
                                className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}
                            >
                                <X className="h-3.5 w-3.5" />
                                Réinitialiser
                            </button>
                        )}
                    </div>

                    {/* rows */}
                    <div className="border-t border-border p-2">
                        {loading ? (
                            <div className="space-y-1">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div key={i} className="flex animate-pulse gap-3 px-3 py-3">
                                        <div className="h-10 w-14 shrink-0 rounded-md bg-surface-sunken" />
                                        <div className="flex-1 space-y-1.5 pt-0.5">
                                            <div className="h-2.5 w-1/4 rounded-full bg-surface-sunken" />
                                            <div className="h-3 w-full rounded-full bg-surface-sunken" />
                                            <div className="h-3 w-3/4 rounded-full bg-surface-sunken" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <p className="text-sm text-ink-muted">
                                    Aucun article{hasActiveFilters ? " pour ces filtres" : ""}
                                </p>
                                {hasActiveFilters && (
                                    <button onClick={resetFilters} className={`mt-3 rounded text-xs text-ink-muted underline ${FOCUS_RING}`}>
                                        Réinitialiser les filtres
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {items.map((item) => (
                                    <ArticleRow
                                        key={item._id}
                                        item={item}
                                        allFranchises={allFranchises}
                                        onSetStatus={handleSetStatus}
                                        onSetCity={handleSetCity}
                                        onSetCategory={handleSetCategory}
                                        onSetImage={handleSetImage}
                                        onScrapeImages={handleScrapeImages}
                                        onSetEstablishment={handleSetEstablishment}
                                        onSetFranchise={handleSetFranchise}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {!loading && items.length > 0 && totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-border px-6 py-4">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className={`text-xs font-medium text-ink-muted transition-colors hover:text-ink disabled:opacity-30 ${FOCUS_RING}`}
                            >
                                ← Précédent
                            </button>
                            <span className="text-xs text-ink-faint">
                                Page {page} / {totalPages} · {total} articles
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className={`text-xs font-medium text-ink-muted transition-colors hover:text-ink disabled:opacity-30 ${FOCUS_RING}`}
                            >
                                Suivant →
                            </button>
                        </div>
                    )}

                    <div className="flex items-start gap-2 border-t border-border px-6 py-4">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-faint" />
                        <p className="text-xs text-ink-muted">
                            Un article approuvé part dans le fil public et peut être repris dans les briefs. Chaque ligne
                            se corrige sur place : catégorie, ville, photo, franchise, et l'établissement auquel elle se
                            rattache — c'est ce lien qui fait remonter l'actualité sur la bonne fiche.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}
